import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuthContext } from "../contexts/AuthContext";
import { ROUTES } from "../constants/routes";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute Component
 *
 * A wrapper component that protects routes by checking authentication status.
 * Redirects to login page if user is not authenticated.
 * Shows loading spinner while authentication status is being determined.
 *
 * @param children - The components to render if user is authenticated
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, isEmailVerified } = useAuthContext();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: "#F5F5F5",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // If not authenticated, redirect to login with the current location
  // This allows us to redirect back to the intended page after login
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  // If authenticated but not email verified, redirect to email verification page
  if (isAuthenticated && !isEmailVerified) {
    return (
      <Navigate
        to={ROUTES.EMAIL_VERIFICATION}
        state={{ from: location }}
        replace
      />
    );
  }

  // If authenticated, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;
